// CancelSubscriptionDialog.tsx — in-app confirm for cancelling a monthly (Stripe)
// subscription. Cancels at the end of the paid period via the cancel-subscription edge
// function; access carries on until then. No external link, on any platform.
// House style: UK English, no emoji, no em-dash.
import React from 'react';
import { Display, Body, Btn } from '@/components/ui';
import { cancelStripeSubscription } from '@/services/sync';

/** "1 November 2026", or null when the value is missing or unparseable. */
export function formatAccessDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function friendlyError(code: string | undefined): string {
  if (code === 'no_active_subscription' || code === 'no_stripe_subscription') {
    return "We couldn't find an active subscription to cancel. If you think this is wrong, email support@renurture.org and we will help.";
  }
  if (code && /network/i.test(code)) return code;
  return 'Something went wrong and your subscription has not been cancelled. Please try again.';
}

export function CancelSubscriptionDialog({
  token,
  periodEnd,
  onClose,
  onCancelled,
}: {
  token: string;
  periodEnd: string | null | undefined;
  onClose: () => void;
  onCancelled: (accessUntilISO: string | null) => void;
}) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const until = formatAccessDate(periodEnd);

  const confirm = async () => {
    if (busy) return;
    setBusy(true); setError(null);
    const res = await cancelStripeSubscription(token);
    setBusy(false);
    if (res.ok) {
      onCancelled(res.accessUntil ?? periodEnd ?? null);
      onClose();
    } else {
      setError(friendlyError(res.error));
    }
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Cancel subscription" style={overlay}>
      <div style={panel}>
        <Display text="Cancel your subscription?" size={26} weight={400} style={{ marginBottom: 12 }} />
        <Body size={14.5} style={{ marginBottom: 6, lineHeight: 1.6 }}>
          {until
            ? <>You&rsquo;ll keep full access until {until}. You won&rsquo;t be charged again.</>
            : <>You&rsquo;ll keep full access until the end of the period you&rsquo;ve paid for. You won&rsquo;t be charged again.</>}
        </Body>
        {error && (
          <Body role="alert" size={13} style={{ marginTop: 10, color: '#8B2F1A', lineHeight: 1.5 }}>{error}</Body>
        )}
        <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
          <Btn full variant="ghost" size="lg" onClick={onClose} disabled={busy}>Keep my membership</Btn>
          <Btn full size="lg" onClick={() => void confirm()} disabled={busy} ariaLabel="Cancel my subscription">
            {busy ? 'Cancelling…' : 'Cancel subscription'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1100,
  background: 'rgba(20,16,12,0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  paddingTop: 'calc(var(--safe-top) + 24px)',
  paddingBottom: 'calc(var(--safe-bottom) + 24px)',
};
const panel: React.CSSProperties = {
  width: '100%',
  maxWidth: 440,
  background: 'var(--card-top, #fff)',
  border: '1px solid var(--hair-strong, rgba(0,0,0,0.1))',
  borderRadius: 20,
  padding: 24,
  textAlign: 'left',
  maxHeight: '90vh',
  overflowY: 'auto',
};
